import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import ym from 'react-yandex-metrika';

import type { RootState } from 'store/store';
import { setGuestId, setLastSeen, setSessionId } from './metric-slice';
import { BackendAnalyticsEvent } from './MetricTypes';
import { api } from '@utils/api';

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 минут
let watchersStarted = false;

/** внутренняя: проверить TTL и, если надо, сгенерить новую сессию — только по Redux-состоянию */
function ensureSessionByState(dispatch: any, getState: () => RootState) {
  const { sessionId, lastSeen } = getState().metric;
  const ts = Date.now();

  const isExpired =
    !sessionId ||
    !lastSeen ||
    Number.isNaN(lastSeen as any) ||
    ts - (lastSeen as number) > SESSION_TTL_MS;

  if (isExpired) {
    const id = uuidv4();
    dispatch(setSessionId(id));
  }
  dispatch(setLastSeen(ts));
}

/** Инициализация метрик на старте приложения */
export const initMetrics = createAsyncThunk<void, void, { state: RootState }>(
  'metric/init',
  async (_arg, { dispatch, getState }) => {
    ensureSessionByState(dispatch, getState);

    if (!watchersStarted) {
      watchersStarted = true;

      // фон: раз в минуту проверяем TTL
      setInterval(() => {
        ensureSessionByState(dispatch, getState);
      }, 60_000);

      // продление по активности пользователя
      const touch = () => ensureSessionByState(dispatch, getState);
      const opts: AddEventListenerOptions | boolean = { passive: true };
      window.addEventListener('visibilitychange', touch as any, opts as any);
      window.addEventListener('pointerdown', touch, opts);
      window.addEventListener('keydown', touch, opts);
      window.addEventListener('scroll', touch, opts);
    }
  },
);

/** Ручной touch сессии (можно дергать перед событиями) */
export const touchSession = createAsyncThunk<void, void, { state: RootState }>(
  'metric/touch',
  async (_arg, { dispatch, getState }) => {
    ensureSessionByState(dispatch, getState);
  },
);

/** Гость/пользователь: создать/сбросить guestId по состоянию авторизации */
export const ensureGuestId = createAsyncThunk<void, void, { state: RootState }>(
  'metric/ensureGuestId',
  async (_arg, { dispatch, getState }) => {
    const { auth, metric } = getState() as RootState & any;
    const isLoggedIn = auth?.isLoggedIn;
    const currentGuest = metric.guestId;

    if (!isLoggedIn && !currentGuest) {
      dispatch(setGuestId(uuidv4()));
    }
  },
);

/** Просмотр страницы (SPA): вызывает touch и отправляет hit */
export const hitPage = createAsyncThunk<
  void,
  { url: string; title?: string; referer?: string; params?: Record<string, any> },
  { state: RootState }
>('metric/hitPage', async ({ url, title, referer, params }, { dispatch }) => {
  await dispatch(touchSession());
  ym('hit', url, { title, referer, params });
});

/** Событие/цель: вызывает touch и отправляет reachGoal (добавит session_id автоматически) */
export const trackGoal = createAsyncThunk<
  void,
  { name: string; params?: Record<string, any> },
  { state: RootState }
>('metric/trackGoal', async ({ name, params }, { dispatch, getState }) => {
  await dispatch(touchSession());
  const { sessionId } = getState().metric;
  const payload = { ...params, session_id: sessionId };
  ym('reachGoal', name, payload);
});

export const sendAnalyticsEvent = createAsyncThunk<void, BackendAnalyticsEvent>(
  'metric/sendAnalyticsEvent',
  async payload => {
    try {
      await api.post(`/analytics/events`, payload);
    } catch (e) {
      console.warn(e);
    }
  },
);
